import { useEffect } from "react"
import config from "aldoo-ra/Config"
const { FACEBOOK_PIXEL, FACEBOOK_OPTIONS, TRACKING_GDPR } = config
import PubSub from "pubsub-js"
import { hasConsent, CONSENT_EVENT } from "./consent"

/**
 * The Meta (Facebook) pixel: page views and the shop's events, from the same
 * Tracking_* topics Google gets (GA4 payloads: value, currency, items).
 *
 * The pixel is the page's (AldooVike Head, from the META_PIXEL_ID parameter:
 * window.__aldooMetaPixel), or the project's config.FACEBOOK_PIXEL, loaded
 * here. Events:
 *   Tracking_PageView → PageView · Tracking_ViewItem → ViewContent ·
 *   Tracking_AddToCart → AddToCart · Tracking_AddToWishlist → AddToWishlist ·
 *   Tracking_BeginCheckout / Tracking_InitiateCheckout → InitiateCheckout ·
 *   Tracking_AddPaymentInfo → AddPaymentInfo · Tracking_Purchase → Purchase ·
 *   Tracking_Subscribed → Subscribe · Tracking_Search → Search ·
 *   Tracking_NewAccountCreated → CompleteRegistration
 */

const SENT_PURCHASES = "aldoo-meta-purchases"

const loadPixel = (pixelId) => {
  if (!window.fbq) {
    // Meta's base code
    const n = (window.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
    })
    if (!window._fbq) window._fbq = n
    n.push = n
    n.loaded = true
    n.version = "2.0"
    n.queue = []
    const script = document.createElement("script")
    script.async = true
    script.src = "https://connect.facebook.net/en_US/fbevents.js"
    document.head.appendChild(script)
  }
  // Revoke before the first hit; the consent banner grants it later
  if (TRACKING_GDPR && !hasConsent()) window.fbq("consent", "revoke")
  window.fbq("init", pixelId, undefined, FACEBOOK_OPTIONS)
}

const money = (value) => (typeof value === "number" && Number.isFinite(value) ? Math.round(value * 100) / 100 : undefined)

/** A GA4 payload as Meta's custom data: the products, their value and currency. */
const contents = (data = {}) => {
  const items = Array.isArray(data.items) ? data.items.filter((item) => item?.item_id) : []
  const value = money(data.value ?? data.price)
  const out = { content_type: "product" }
  if (items.length) {
    out.content_ids = items.map((item) => String(item.item_id))
    out.contents = items.map((item) => ({ id: String(item.item_id), quantity: item.quantity || 1, ...(money(item.price) !== undefined ? { item_price: money(item.price) } : {}) }))
    out.num_items = items.reduce((sum, item) => sum + (item.quantity || 1), 0)
    if (items.length === 1 && items[0].item_name) out.content_name = items[0].item_name
  } else if (data.pid) {
    out.content_ids = [String(data.pid)]
  }
  if (value !== undefined) out.value = value
  if (data.currency) out.currency = String(data.currency).toUpperCase()
  return out
}

/** A purchase is reported once, however often its page is shown. */
const firstReport = (transactionId) => {
  if (!transactionId) return true
  try {
    const sent = JSON.parse(localStorage.getItem(SENT_PURCHASES) || "[]")
    if (sent.includes(transactionId)) return false
    localStorage.setItem(SENT_PURCHASES, JSON.stringify([...sent, transactionId].slice(-50)))
  } catch (e) {
    // storage unavailable: reported, and not remembered
  }
  return true
}

export default function FacebookTracking({ children }) {
  useEffect(() => {
    const inPage = typeof window !== "undefined" && window.__aldooMetaPixel
    if (!inPage && !FACEBOOK_PIXEL) return
    if (!inPage) loadPixel(FACEBOOK_PIXEL)

    // eventID: the transaction, so a server report of the same purchase is deduplicated
    const track = (name, params, eventID) => window.fbq?.("track", name, params, eventID ? { eventID } : undefined)
    let checkoutAt = 0

    const on = (topic, handler) => PubSub.subscribe(topic, (_, data) => handler(data || {}))
    const tokens = [
      on(CONSENT_EVENT, (value) => window.fbq?.("consent", value === "granted" ? "grant" : "revoke")),
      on("Tracking_PageView", () => track("PageView")),
      on("Tracking_ViewItem", (data) => track("ViewContent", contents(data))),
      on("Tracking_AddToCart", (data) => track("AddToCart", contents(data))),
      on("Tracking_AddToWishlist", (data) => track("AddToWishlist", contents(data))),
      on("Tracking_BeginCheckout", (data) => {
        checkoutAt = Date.now()
        track("InitiateCheckout", contents(data))
      }),
      // the older topic; a store that publishes both is counted once
      on("Tracking_InitiateCheckout", (data) => Date.now() - checkoutAt > 3000 && track("InitiateCheckout", contents(data))),
      on("Tracking_AddPaymentInfo", (data) => track("AddPaymentInfo", contents(data))),
      on("Tracking_Purchase", (data) => {
        const id = data.transactionId || data.transaction_id
        if (!firstReport(id)) return
        track("Purchase", { ...contents(data), currency: contents(data).currency || "EUR", value: contents(data).value ?? 0 }, id)
      }),
      on("Tracking_Subscribed", (data) => track("Subscribe", { ...contents(data), currency: contents(data).currency || "EUR", value: contents(data).value ?? 0 })),
      on("Tracking_Search", (data) => data.search_term && track("Search", { search_string: String(data.search_term).slice(0, 100) })),
      on("Tracking_NewAccountCreated", () => track("CompleteRegistration")),
    ]
    return () => tokens.forEach((token) => PubSub.unsubscribe(token))
  }, [])

  return children || null
}
